import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Grid,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  createFilterOptions,
  Chip,
} from "@mui/material";
import { UrlApi } from "../../../services/apirest";
import Notify from "../../Notify";
import LinkPagoGeneradoGranel from "./componentes/LinkPagoGeneradoGranel";
import {
  crearLinkGranel,
  anularLinkGranel,
  mensajeError,
} from "./services/linkGranelMp.service";

const REFERENCIA_MAX = 20;
// Condición de pago del cliente que permite vincular el link a un pedido
const CONDICION_PAGO_ANTICIPADO = 2;

const referenciaDePedido = (pedido) => `Pedido ${pedido.id_pedido}`.slice(0, REFERENCIA_MAX);

const pesos = (valor) =>
  `$${Number(valor).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const numero = (valor) => Number(valor).toLocaleString("es-AR", { maximumFractionDigits: 2 });
const fechaDMA = (fecha) => (fecha ? fecha.split("-").reverse().join("-") : "");

// Detalle del monto sugerido (datos que devuelve /granel_link_pago/sugerido)
function MontoSugerido({ sugerido, cargando, onUsar }) {
  if (cargando) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2">Calculando monto sugerido...</Typography>
      </Box>
    );
  }
  if (!sugerido) return null;
  const { unidad, cantidad, precio_unitario: precio, importe } = sugerido;
  const origen = sugerido.origen_precio === "especial" ? "precio especial" : "lista de precios";
  const motivoFecha = sugerido.mantiene_precio ? "mantiene precio: fecha de registro" : "precio vigente hoy";

  return (
    <Alert
      severity={importe ? "info" : "warning"}
      action={
        importe ? (
          <Button color="inherit" size="small" onClick={() => onUsar(importe)}>
            Usar este monto
          </Button>
        ) : null
      }
    >
      <Typography variant="body2" fontWeight={700}>
        Monto sugerido: {importe ? pesos(importe) : "no se pudo calcular"}
      </Typography>
      {cantidad && precio ? (
        <Typography variant="body2">
          {numero(cantidad)} {unidad} × {pesos(precio)}/{unidad}
        </Typography>
      ) : null}
      <Typography variant="caption" display="block">
        {origen} vigente al {fechaDMA(sugerido.fecha_precio)} ({motivoFecha})
        {sugerido.kg ? ` · ${numero(sugerido.kg)} kg` : ""}
        {sugerido.lts ? ` · ${numero(sugerido.lts)} lts` : ""}
        {sugerido.densidad ? ` · densidad ${sugerido.densidad}` : ""}
        {sugerido.situacion_iva_texto ? ` · ${sugerido.situacion_iva_texto}` : ""}
      </Typography>
      {sugerido.advertencias?.map((texto) => (
        <Typography key={texto} variant="caption" display="block" fontWeight={700}>
          {texto}
        </Typography>
      ))}
    </Alert>
  );
}

const textoPedido = (p) =>
  [
    `#${p.id_pedido}`,
    p.estado,
    p.kgacargar ? `${Number(p.kgacargar).toLocaleString("es-AR")} kg` : null,
    p.fecha_requerida ? `requerido ${p.fecha_requerida.split("-").reverse().join("-")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const textoCliente = (c) => (c?.razon_social ? `${c.razon_social} - ${c.id}` : "");

const detalleCliente = (c) =>
  [
    c.sisven_id ? `Sisven: ${c.sisven_id}` : null,
    c.cuit ? `CUIT: ${c.cuit}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

// Color del chip de empresa según clientes.id_empresa
const COLOR_EMPRESA = {
  1: "#1F618D", // Hipergas: azul institucional (mismo del encabezado)
  4: "#1E8449", // id 4: verde sobrio
};
const COLOR_EMPRESA_DEFAULT = "#5D6D7E"; // gris pizarra para cualquier otra
const colorEmpresa = (idEmpresa) => COLOR_EMPRESA[Number(idEmpresa)] || COLOR_EMPRESA_DEFAULT;

// Búsqueda por razón social, id, sisven_id, cuit (con y sin guiones) y empresa.
// El texto de cada cliente se arma una sola vez (cache por objeto) y se muestran como
// máximo CLIENTES_MAX_OPCIONES, para no renderizar miles de opciones a la vez.
const CLIENTES_MAX_OPCIONES = 50;
const textoBusquedaCliente = new WeakMap();
const filtrarClientes = createFilterOptions({
  limit: CLIENTES_MAX_OPCIONES,
  stringify: (c) => {
    let texto = textoBusquedaCliente.get(c);
    if (texto === undefined) {
      texto = [
        c.razon_social,
        c.id,
        c.sisven_id,
        c.cuit,
        c.cuit ? String(c.cuit).replace(/\D/g, "") : null,
        c.empresa,
      ]
        .filter((v) => v !== null && v !== undefined && v !== "")
        .join(" ");
      textoBusquedaCliente.set(c, texto);
    }
    return texto;
  },
});

// Cuotas (solo admin puede cambiarlas; si no, la API usa el default: 12, sin interés 2 a 6)
const CUOTAS_MAX_PERMITIDAS = 12;
const CUOTAS_DEFAULT = { max: 12, sinInteresHasta: 6 };
const SIN_INTERES_NINGUNA = "ninguna";
// Topes de cuotas sin interés que acepta Mercado Pago (otro valor da error)
const CUOTAS_SIN_INTERES_PERMITIDAS = [3, 6, 9, 12];

const GenerarLinkPagoGranel = ({ open, onClose, onGenerado }) => {
  const esAdmin = localStorage.getItem("role") === "admin";

  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  // Pedido opcional (solo clientes de pago anticipado)
  const [pedidos, setPedidos] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [cargandoPedidos, setCargandoPedidos] = useState(false);
  // Monto sugerido del pedido elegido (editable: solo se copia al monto con "Usar este monto")
  const [sugerido, setSugerido] = useState(null);
  const [cargandoSugerido, setCargandoSugerido] = useState(false);
  const [monto, setMonto] = useState("");
  const [referencia, setReferencia] = useState("");
  const [cuotasMax, setCuotasMax] = useState(CUOTAS_DEFAULT.max);
  const [sinInteresHasta, setSinInteresHasta] = useState(CUOTAS_DEFAULT.sinInteresHasta);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [notify, setNotify] = useState({
    open: false,
    mensaje: "",
    severity: "success",
    color: "#D4EFDF",
  });

  useEffect(() => {
    if (!open) return;
    // Reset al abrir
    setCliente(null);
    setPedidos([]);
    setPedido(null);
    setMonto("");
    setReferencia("");
    setCuotasMax(CUOTAS_DEFAULT.max);
    setSinInteresHasta(CUOTAS_DEFAULT.sinInteresHasta);
    setError(null);
    setPaymentLink(null);
    setLinkCopied(false);

    // /activos: la empresa que trae es la del cliente (la misma que usa el link de MP)
    fetch(UrlApi + "/clientes/activos")
      .then((res) => res.json())
      .then((data) => setClientes(Array.isArray(data) ? data : []))
      .catch(() => setClientes([]));
  }, [open]);

  const esPagoAnticipado = Number(cliente?.id_condicionpago) === CONDICION_PAGO_ANTICIPADO;

  // Pedidos que se pueden vincular: al cambiar el cliente se cargan de nuevo
  useEffect(() => {
    setPedido(null);
    setPedidos([]);
    if (!cliente || !esPagoAnticipado) return;
    let vigente = true;
    setCargandoPedidos(true);
    fetch(UrlApi + "/granel_link_pago/pedidos_disponibles/" + cliente.id)
      .then((res) => res.json())
      .then((data) => vigente && setPedidos(Array.isArray(data) ? data : []))
      .catch(() => vigente && setPedidos([]))
      .finally(() => vigente && setCargandoPedidos(false));
    return () => {
      vigente = false;
    };
  }, [cliente, esPagoAnticipado]);

  // Monto sugerido: se calcula al elegir un pedido
  useEffect(() => {
    setSugerido(null);
    if (!pedido) return;
    let vigente = true;
    setCargandoSugerido(true);
    fetch(UrlApi + "/granel_link_pago/sugerido/" + pedido.id_pedido)
      .then((res) => res.json())
      .then((data) => vigente && setSugerido(data && data.id_pedido ? data : null))
      .catch(() => vigente && setSugerido(null))
      .finally(() => vigente && setCargandoSugerido(false));
    return () => {
      vigente = false;
    };
  }, [pedido]);

  const elegirPedido = (valor) => {
    setPedido(valor);
    // La referencia se completa con el pedido (se puede editar)
    if (valor) setReferencia(referenciaDePedido(valor));
  };

  const montoNum = Number(monto);
  const formValido =
    cliente &&
    Number.isFinite(montoNum) &&
    Number(montoNum.toFixed(2)) > 0 &&
    referencia.trim() !== "" &&
    referencia.length <= REFERENCIA_MAX;

  const handleGenerar = async () => {
    if (!formValido) return;
    setLoading(true);
    setError(null);

    let idRegistro = null;
    // La empresa (cuenta de Mercado Pago) la define el backend con clientes.id_empresa
    let idEmpresa = null;
    const textoReferencia = referencia.trim();
    try {
      // 1. Crear el registro en la base (estado 'generado'). Si se eligió un pedido, el backend
      // valida que se pueda vincular y que no tenga otro link generado o pagado.
      const bodyRegistro = {
        id_cliente: cliente.id,
        id_pedido: pedido ? pedido.id_pedido : null,
        monto: Number(montoNum.toFixed(2)),
        referencia: textoReferencia || null,
        usuario: localStorage.getItem("user"),
      };

      const respRegistro = await fetch(UrlApi + "/granel_link_pago/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyRegistro),
      });
      const dataRegistro = await respRegistro.json().catch(() => ({}));
      if (!respRegistro.ok) {
        throw new Error(dataRegistro.message || "No se pudo registrar el link de pago");
      }
      idRegistro = dataRegistro.id;
      idEmpresa = dataRegistro.id_empresa ?? null;

      // 2. Generar el link en Mercado Pago. La API toma monto y referencia del
      // registro, arma el external_reference (glp_<id>) y guarda link y order_id
      // en el registro.
      // Solo un admin envía cuotas; para el resto la API aplica el default
      const cuotas = esAdmin
        ? {
            max: cuotasMax,
            sinInteresHasta:
              sinInteresHasta === SIN_INTERES_NINGUNA ? null : sinInteresHasta,
          }
        : undefined;
      const { checkout_url, order_id, external_reference } = await crearLinkGranel(
        idEmpresa,
        idRegistro,
        cuotas
      );

      setPaymentLink({
        url: checkout_url,
        external_reference,
        order_id,
        referencia: textoReferencia,
      });
      setNotify({
        open: true,
        mensaje: "Link de pago generado",
        severity: "success",
        color: "#D4EFDF",
      });
      onGenerado && onGenerado();
    } catch (err) {
      console.error("Error generando link de pago granel:", err);
      // Si el registro quedó creado, se anula para no dejarlo huérfano. La API
      // además cancela la order en MP si llegó a crearse; si la API no responde,
      // se anula directo en la base.
      if (idRegistro) {
        anularLinkGranel(idEmpresa, idRegistro).catch(() =>
          fetch(UrlApi + "/granel_link_pago/anular/" + idRegistro, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
          }).catch(() => {})
        );
      }
      setError(mensajeError(err, "Error generando el link de pago"));
    } finally {
      setLoading(false);
    }
  };

  const copiarLink = () => {
    if (!paymentLink?.url) return;
    navigator.clipboard
      .writeText(paymentLink.url)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => setError("No se pudo copiar al portapapeles"));
  };

  return (
    <>
      {/* Mientras se genera el link no se permite cerrar, para no cortar el proceso a mitad */}
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
        <Box backgroundColor="#1F618D">
          <DialogTitle>
            <Typography variant="h6" align="center" color="#ffffff">
              Generar link de pago
            </Typography>
          </DialogTitle>
        </Box>

        <DialogContent dividers>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : paymentLink ? (
            <LinkPagoGeneradoGranel
              paymentLink={paymentLink}
              linkCopied={linkCopied}
              onCopiarLink={copiarLink}
            />
          ) : (
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12}>
                <Autocomplete
                  options={clientes}
                  value={cliente}
                  noOptionsText="Cliente inexistente"
                  getOptionLabel={textoCliente}
                  filterOptions={filtrarClientes}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(e, value) => setCliente(value)}
                  renderOption={(props, c) => (
                    <li {...props} key={c.id}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                        width="100%"
                      >
                        <Box>
                          <Typography variant="body2">{textoCliente(c)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {detalleCliente(c)}
                          </Typography>
                        </Box>
                        {c.empresa ? (
                          <Chip
                            label={c.empresa}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: colorEmpresa(c.id_empresa),
                              borderColor: colorEmpresa(c.id_empresa),
                              fontWeight: 500,
                              flexShrink: 0,
                            }}
                          />
                        ) : null}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Tipee para buscar cliente.."
                    />
                  )}
                />
              </Grid>

              {cliente ? (
                <Grid item xs={12}>
                  {esPagoAnticipado ? (
                    <Autocomplete
                      options={pedidos}
                      value={pedido}
                      loading={cargandoPedidos}
                      noOptionsText="El cliente no tiene pedidos pendientes, aprobados ni asignados"
                      getOptionLabel={(p) => (p ? textoPedido(p) : "")}
                      getOptionDisabled={(p) => !!p.id_link_activo}
                      isOptionEqualToValue={(opt, val) => opt.id_pedido === val.id_pedido}
                      onChange={(e, value) => elegirPedido(value)}
                      renderOption={(props, p) => (
                        <li {...props} key={p.id_pedido}>
                          <Box>
                            <Typography variant="body2">{textoPedido(p)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {p.id_link_activo
                                ? `Ya tiene el link N° ${p.id_link_activo}`
                                : p.direccion || ""}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Pedido (opcional)"
                          placeholder="Sin pedido"
                          helperText="Pedidos pendientes, aprobados o asignados del cliente"
                        />
                      )}
                    />
                  ) : (
                    <Alert severity="info">
                      El cliente no es de pago anticipado: el link no se puede vincular a un pedido.
                    </Alert>
                  )}
                </Grid>
              ) : null}

              {pedido ? (
                <Grid item xs={12}>
                  <MontoSugerido
                    sugerido={sugerido}
                    cargando={cargandoSugerido}
                    onUsar={(importe) => setMonto(String(importe))}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monto"
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Referencia"
                  value={referencia}
                  onChange={(e) =>
                    setReferencia(e.target.value.slice(0, REFERENCIA_MAX))
                  }
                  inputProps={{ maxLength: REFERENCIA_MAX }}
                  helperText={`${referencia.length}/${REFERENCIA_MAX}`}
                />
              </Grid>

              {esAdmin && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Cuotas máximas"
                      value={cuotasMax}
                      onChange={(e) => {
                        const max = Number(e.target.value);
                        setCuotasMax(max);
                        // El rango sin interés no puede superar el máximo de cuotas
                        // (se pasa al mayor tope permitido que entre, o a ninguna)
                        if (sinInteresHasta !== SIN_INTERES_NINGUNA && sinInteresHasta > max) {
                          const tope = CUOTAS_SIN_INTERES_PERMITIDAS.filter((n) => n <= max).pop();
                          setSinInteresHasta(tope || SIN_INTERES_NINGUNA);
                        }
                      }}
                    >
                      {Array.from({ length: CUOTAS_MAX_PERMITIDAS }, (_, i) => i + 1).map((n) => (
                        <MenuItem key={n} value={n}>
                          {n === 1 ? "1 (sin cuotas)" : n}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Sin interés"
                      value={sinInteresHasta}
                      onChange={(e) =>
                        setSinInteresHasta(
                          e.target.value === SIN_INTERES_NINGUNA
                            ? SIN_INTERES_NINGUNA
                            : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value={SIN_INTERES_NINGUNA}>Ninguna (todas con interés)</MenuItem>
                      {CUOTAS_SIN_INTERES_PERMITIDAS.filter((n) => n <= cuotasMax).map((n) => (
                        <MenuItem key={n} value={n}>
                          De 2 a {n} cuotas
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="error" variant="outlined" disabled={loading}>
            {paymentLink ? "Cerrar" : "Cancelar"}
          </Button>
          {!paymentLink && (
            <Button
              onClick={handleGenerar}
              color="success"
              variant="contained"
              disabled={!formValido || loading}
            >
              Generar link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {notify.open && (
        <Notify
          mensaje={notify.mensaje}
          open={notify.open}
          color={notify.color}
          severity={notify.severity}
          handleClose={() => setNotify({ ...notify, open: false })}
        />
      )}
    </>
  );
};

export default GenerarLinkPagoGranel;
